import { Check, Flag, Lock } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { BulkActionBar } from "@/components/cinch/BulkActionBar";
import {
  DataTable,
  type DataTableColumn,
} from "@/components/cinch/DataTable/DataTable";
import {
  ActiveFilterPill,
  CheckboxList,
  FilterBar,
  FilterChip,
  FilterClearAll,
  FilterSearch,
} from "@/components/cinch/FilterBar";
import { IconButton } from "@/components/cinch/IconButton";
import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import {
  useBillingValidation,
  useBulkReviewBillingValidation,
  useReviewBillingValidation,
  type BillingValidationItem,
  type ReviewStatus,
} from "../api";
import { fmtCur2 } from "../../renewals/format";
import { BillingValidationDrawer } from "./BillingValidationDrawer";

const REVIEW_LABELS: { value: ReviewStatus; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "flagged", label: "Flagged" },
];

/** R-15 worklist: every structural break in the billing graph, live.
 *
 *  No pull. These rows are exception lists, and the point of an exception list
 *  is that a row LEAVES it when someone fixes the thing — so the list reads
 *  Snowflake on every load and only the verdict persists. Completing the step
 *  freezes whatever was still broken as the audit copy. */
export function BillingValidationPanel({ period }: { period: string }) {
  const { data, isLoading, error } = useBillingValidation(period);
  const review = useReviewBillingValidation(period);
  const bulk = useBulkReviewBillingValidation(period);

  const [tab, setTab] = useState<string>("");
  const [reviewFilter, setReviewFilter] = useState<string[]>([]);
  const [billingOnly, setBillingOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [drawerKey, setDrawerKey] = useState<string | null>(null);
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const [flagIntent, setFlagIntent] = useState(false);

  const items = useMemo(() => data?.items ?? [], [data]);
  const checks = useMemo(() => data?.checks ?? [], [data]);
  const summary = data?.summary;
  const locked = data?.locked ?? false;
  const canAct = (data?.can_act ?? false) && !locked;
  const countFor = (key: string) => summary?.counts_by_check?.[key] ?? 0;

  // Only checks with something wrong get a tab — a strip of green zeroes is
  // noise on a page whose job is showing you what's broken.
  const liveChecks = useMemo(
    () => checks.filter((c) => countFor(c.key) > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checks, summary],
  );
  const activeKey = liveChecks.some((c) => c.key === tab)
    ? tab
    : (liveChecks[0]?.key ?? "");
  const active = checks.find((c) => c.key === activeKey) ?? null;

  const rows = useMemo(() => {
    let out = items.filter((i) => i.check_key === activeKey);
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((i) =>
        `${i.entity_name ?? ""} ${i.related_name ?? ""}`.toLowerCase().includes(q),
      );
    }
    if (reviewFilter.length) out = out.filter((i) => reviewFilter.includes(i.review_status));
    if (billingOnly) out = out.filter((i) => i.is_billing);
    return out;
  }, [items, activeKey, search, reviewFilter, billingOnly]);

  const inTab = useMemo(
    () => items.filter((i) => i.check_key === activeKey).length,
    [items, activeKey],
  );
  // Looked up across ALL items, not just the tab's — a verdict save refetches
  // and the row could momentarily fall outside the current filters.
  const drawerItem = useMemo(
    () => items.find((i) => i.item_key === drawerKey) ?? null,
    [items, drawerKey],
  );

  /** Same two-doors idiom as the other worklists: the row opens the quick view
   *  to record a verdict, the name opens the full record to go and fix it. */
  function openDrawer(i: BillingValidationItem, flag: boolean, expanded = false) {
    setFlagIntent(flag);
    setDrawerKey(i.item_key);
    setDrawerExpanded(expanded);
  }

  function quickReview(i: BillingValidationItem, status: ReviewStatus) {
    review.mutate(
      { itemKey: i.item_key, status },
      {
        onError: (e) =>
          toast.error("Couldn't save that review", {
            description: (e as Error).message,
          }),
      },
    );
  }

  async function bulkReview(status: ReviewStatus) {
    const todo = rows.filter((i) => i.review_status !== status);
    if (!todo.length) {
      toast.info(`Nothing to ${status === "approved" ? "approve" : "flag"}.`);
      return;
    }
    try {
      const res = await bulk.mutateAsync({
        itemKeys: todo.map((i) => i.item_key),
        status,
      });
      toast.success(
        `${status === "approved" ? "Approved" : "Flagged"} ${res.updated} of ${res.requested}`,
      );
    } catch (e) {
      toast.error("Bulk review failed", { description: (e as Error).message });
    }
  }

  // Problem and Type are constant within a tab, so they'd be the same value
  // repeated down the page — the tab already says which check you're in.
  const columns: DataTableColumn<BillingValidationItem>[] = [
    {
      key: "entity_name",
      // The tab fixes the record type, so the column can just name it.
      header: active?.entity
        ? active.entity.charAt(0).toUpperCase() + active.entity.slice(1)
        : "Record",
      className: "max-w-[360px]",
      render: (i) => (
        <button
          type="button"
          className="block max-w-full truncate text-left text-sm font-semibold text-brand hover:underline"
          title={`${i.entity_name ?? i.entity_id} — open the full ${i.entity} record`}
          onClick={(e) => {
            e.stopPropagation();
            openDrawer(i, false, true);
          }}
        >
          {i.entity_name ?? <span className="font-mono text-xs">{i.entity_id.slice(0, 8)}…</span>}
        </button>
      ),
    },
    {
      key: "related_name",
      header: "Detail",
      className: "max-w-[240px]",
      render: (i) => (
        <span className="block truncate text-xs text-muted-foreground" title={i.related_name ?? undefined}>
          {i.related_name ?? "—"}
        </span>
      ),
    },
    {
      key: "billed",
      header: "Billing",
      numeric: true,
      render: (i) =>
        i.is_billing ? (
          <span className="font-semibold text-danger" title="This account is taking money with no contract behind it">
            {fmtCur2(i.billed)}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "review",
      header: "Review",
      align: "right",
      className: "w-36",
      render: (i) => (
        <span className="inline-flex items-center justify-end gap-1.5">
          <StatusBadge value={i.review_status} />
          {canAct && (
            <>
              <IconButton
                title="Accept — reviewed, and this one is fine as it stands"
                active={i.review_status === "approved"}
                activeClassName="text-success"
                hoverClassName="hover:bg-success-light hover:text-success"
                disabled={review.isPending}
                onClick={() => quickReview(i, "approved")}
              >
                <Check className="h-3.5 w-3.5" />
              </IconButton>
              <IconButton
                title="Flag — must be fixed (opens notes); blocks completing this step"
                active={i.review_status === "flagged"}
                activeClassName="text-warning"
                hoverClassName="hover:bg-warning-light hover:text-warning"
                onClick={() => openDrawer(i, true)}
              >
                <Flag className="h-3.5 w-3.5" />
              </IconButton>
            </>
          )}
        </span>
      ),
    },
  ];

  if (isLoading && !data) return <Card className="h-64 animate-pulse" />;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">
          {locked && data?.frozen_at
            ? `Frozen ${new Date(data.frozen_at).toLocaleString()} when the step was signed off`
            : "Live from Snowflake — fix a record in Cinch and it drops off this list."}
        </div>
        {locked && (
          <span className="flex items-center gap-1 text-xs font-medium text-success">
            <Lock className="h-3.5 w-3.5" /> Signed off — frozen
          </span>
        )}
      </div>

      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}
      >
        <KpiTile
          label="Structural exceptions"
          value={String(summary?.exception_count ?? 0)}
          sub="across every check"
        />
        <KpiTile
          label="Billing with no contract"
          value={String(summary?.billing_without_contract_count ?? 0)}
          tone={summary?.billing_without_contract_count ? "danger" : "positive"}
          info="Accounts taking money with no contract behind them. The one check here that is unambiguously costing or hiding revenue right now — everything else is structure."
          sub="revenue with nothing behind it"
          onClick={() => {
            // Only meaningful on one check, so take the reviewer there rather
            // than filtering a tab where every row would drop out.
            setTab("account_no_contract");
            setBillingOnly((v) => !v);
          }}
          selected={billingOnly}
        />
        <KpiTile
          label="Awaiting review"
          value={String(summary?.pending_count ?? 0)}
          tone={summary?.pending_count ? "warning" : "positive"}
        />
        <KpiTile
          label="Flagged"
          value={String(summary?.flagged_count ?? 0)}
          tone={summary?.flagged_count ? "danger" : "positive"}
          info="An open flag blocks completing this step — completing it is the claim that the billing graph hangs together."
          onClick={() =>
            setReviewFilter(reviewFilter.includes("flagged") ? [] : ["flagged"])
          }
          selected={reviewFilter.includes("flagged")}
        />
      </div>

      {error != null && (
        <Alert variant="danger">
          <AlertTitle>Couldn't load the billing checks</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : String(error)}
          </AlertDescription>
        </Alert>
      )}

      {/* One tab per problem type that HAS something wrong, each carrying its
          count. Checks that pass drop out entirely — the page's job is showing
          what's broken, and a row of green zeroes competes with that. */}
      {liveChecks.length > 0 && (
        <div className="flex flex-wrap gap-1 border-b border-border">
          {liveChecks.map((c) => {
            const on = c.key === activeKey;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setTab(c.key)}
                className={cn(
                  "flex items-center gap-1.5 rounded-t-md border-b-2 px-3 py-1.5 text-sm font-medium transition-colors",
                  on
                    ? "border-brand text-brand"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {c.short_title}
                <span
                  className={cn(
                    "rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums",
                    c.severity === "danger"
                      ? "bg-danger-light text-danger"
                      : "bg-warning-light text-warning",
                  )}
                >
                  {countFor(c.key)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {liveChecks.length === 0 && !isLoading && (
        <Alert variant="success">
          <AlertTitle>Every check passes</AlertTitle>
          <AlertDescription>
            Nothing in the billing graph is broken this month — every company,
            account, contract and purchase order is connected to what it should
            be.
          </AlertDescription>
        </Alert>
      )}

      {active && (
        <Alert variant={active.severity === "danger" ? "danger" : "warning"}>
          <AlertTitle>{active.title}</AlertTitle>
          <AlertDescription>
            {active.why} <b>Fix:</b> {active.fix}
          </AlertDescription>
        </Alert>
      )}

      <FilterBar>
        <FilterSearch
          placeholder="Search within this check…"
          value={search}
          onChange={setSearch}
        />
        <FilterChip label="Review" count={reviewFilter.length}>
          <CheckboxList
            options={REVIEW_LABELS}
            selected={reviewFilter}
            onChange={setReviewFilter}
          />
        </FilterChip>
        {billingOnly && (
          <ActiveFilterPill
            label="Billing with no contract"
            onRemove={() => setBillingOnly(false)}
          />
        )}
        {(reviewFilter.length > 0 || billingOnly || search) && (
          <FilterClearAll
            onClick={() => {
              setReviewFilter([]);
              setBillingOnly(false);
              setSearch("");
            }}
          />
        )}
      </FilterBar>

      {canAct && (
        <BulkActionBar
          count={rows.length}
          totalCount={inTab}
          noun="exception"
          nounPlural="exceptions"
          actions={[
            {
              label: "Accept",
              tone: "success",
              icon: <Check className="h-3.5 w-3.5" />,
              disabled: bulk.isPending,
              onClick: () => bulkReview("approved"),
            },
            {
              label: "Flag",
              tone: "warning",
              icon: <Flag className="h-3.5 w-3.5" />,
              disabled: bulk.isPending,
              onClick: () => bulkReview("flagged"),
            },
          ]}
        />
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(i) => i.item_key}
        loading={isLoading}
        emptyMessage={
          inTab === 0
            ? "This check passes — nothing is broken here."
            : "No rows match within this check."
        }
        onRowClick={(i) => openDrawer(i, false)}
        selectedKey={drawerKey}
        containerClassName="max-h-[calc(100vh-18rem)]"
      />

      <p className="text-xs text-muted-foreground">
        Click a row to review it, or the name to open the full record and see
        what's missing. Nothing is corrected here — fix it in Cinch and it drops
        off this list on the next load. <b>Accept</b> a row you've reviewed and
        are happy to leave; <b>flag</b> one that has to be fixed before the month
        closes.
      </p>

      <BillingValidationDrawer
        period={period}
        item={drawerItem}
        check={drawerItem ? (checks.find((c) => c.key === drawerItem.check_key) ?? null) : null}
        canAct={canAct}
        expanded={drawerExpanded}
        presetFlag={flagIntent}
        onExpandedChange={setDrawerExpanded}
        onClose={() => {
          setDrawerKey(null);
          setDrawerExpanded(false);
          setFlagIntent(false);
        }}
      />
    </div>
  );
}
